<script setup lang="ts">
import { computed, ref } from 'vue';
import type { ServerProcesses } from '@atum-remote/protocol/server-inspection';
import { bytes, processRows } from '../lib/server-inspection.js';
import { duration, processCpu, processStates } from '../lib/server-resources.js';

type Row = NonNullable<ServerProcesses['processes']>[number];
const props = defineProps<{ reading: ServerProcesses | undefined; busy: boolean }>();
const sort = ref<'cpu' | 'memory'>('cpu');
const expanded = ref(false);
/** Hosts released before structured rows send ps output with lifetime-average CPU. */
const legacy = computed(() => !!props.reading && !props.reading.processes);
const rows = computed<Row[]>(() => {
  const r = props.reading;
  if (!r) return [];
  if (r.processes) return processCpu(r.processes, sort.value);
  const parsed = processRows(r.output.stdout, r.output.truncated || r.output.output_incomplete);
  return processCpu(
    parsed.map((p) => ({
      pid: Number(p.pid),
      name: p.name,
      user: p.user,
      state: '',
      cpu: p.cpu,
      rss: p.bytes,
      threads: 0,
      runtimeSeconds: null,
    })),
    sort.value,
  );
});
const visible = computed(() => (expanded.value ? rows.value : rows.value.slice(0, 15)));
const cpuText = (row: Row) => (row.cpu == null ? '—' : `${row.cpu.toFixed(1)}%`);
</script>

<template>
  <section class="panel res-section" aria-labelledby="res-process-title">
    <div class="res-section-head">
      <div>
        <h3 id="res-process-title">
          Processes
          <span v-if="reading?.processCount" class="tag"
            >{{ reading.processCount.toLocaleString() }} total</span
          >
        </h3>
        <p v-if="legacy">
          CPU is each process's lifetime average on this host version. Update ATUM Remote on the
          server for live CPU.
        </p>
        <p v-else>
          CPU is measured over the last
          {{ reading?.cpuWindowMs ? `${(reading.cpuWindowMs / 1000).toFixed(1)} s` : 'reading' }}
          as a share of one logical CPU. Names only: command lines and environments are never
          collected.
        </p>
      </div>
      <fieldset class="segmented" aria-label="Sort processes by">
        <label><input v-model="sort" type="radio" name="res-process-sort" value="cpu" />CPU</label>
        <label
          ><input v-model="sort" type="radio" name="res-process-sort" value="memory" />Memory</label
        >
      </fieldset>
    </div>
    <div class="server-table res-table">
      <table>
        <thead>
          <tr>
            <th>Process</th>
            <th class="num">PID</th>
            <th>User</th>
            <th v-if="!legacy">State</th>
            <th class="num" :aria-sort="sort === 'cpu' ? 'descending' : undefined">
              {{ legacy ? 'CPU avg.' : 'CPU' }}
            </th>
            <th class="num" :aria-sort="sort === 'memory' ? 'descending' : undefined">Memory</th>
            <th v-if="!legacy" class="num">Threads</th>
            <th v-if="!legacy" class="num">Running for</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in visible" :key="p.pid">
            <td class="mono">{{ p.name }}</td>
            <td class="num mono">{{ p.pid }}</td>
            <td>{{ p.user }}</td>
            <td v-if="!legacy" :title="processStates[p.state] ?? p.state">
              <span
                class="res-state"
                :data-tone="p.state === 'D' || p.state === 'Z' ? 'warn' : 'muted'"
                >{{ processStates[p.state] ?? p.state }}</span
              >
            </td>
            <td class="num">{{ cpuText(p) }}</td>
            <td class="num">{{ bytes(p.rss) }}</td>
            <td v-if="!legacy" class="num">{{ p.threads }}</td>
            <td v-if="!legacy" class="num">{{ duration(p.runtimeSeconds) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-if="!rows.length && !busy" class="res-muted res-gap">No process data was returned.</p>
    <button
      v-if="rows.length > 15"
      type="button"
      class="quiet small res-more"
      :aria-expanded="expanded ? 'true' : 'false'"
      @click="expanded = !expanded"
    >
      {{ expanded ? 'Show top 15' : `Show all ${rows.length} listed` }}
    </button>
  </section>
</template>
